<template>
  <v-app>
    <div v-if="customerStore.impersonating" class="impersonate-toolbar">
      <div class="imp-inner">
        <v-icon size="16" color="white">mdi-eye-outline</v-icon>
        <span>Ansicht als <strong>{{ customerStore.customer?.company || customerStore.customer?.email }}</strong></span>
        <button class="imp-exit" @click="exitImpersonate"><v-icon size="14">mdi-close</v-icon> Beenden</button>
      </div>
    </div>

    <v-app-bar v-if="mobile" color="#0a1628" elevation="0" class="mobile-app-bar" :style="{ top: impOffset + 'px' }">
      <v-app-bar-nav-icon color="white" @click="drawer = !drawer"></v-app-bar-nav-icon>
      <v-img src="@/assets/logo.png" max-width="100" contain style="cursor: pointer" @click="$router.push('/')" />
      <v-spacer />
      <v-btn icon size="small" color="white" @click="logout"><v-icon>mdi-logout</v-icon></v-btn>
    </v-app-bar>

    <v-navigation-drawer v-model="drawer" :permanent="!mobile" :temporary="mobile" location="left" width="280" class="drawer-custom" color="#0a1628" :border="0"
      :style="{ top: impOffset + 'px', height: `calc(100vh - ${impOffset}px)` }">
      <div class="drawer-inner">
        <div class="px-5 py-6"><v-img src="@/assets/logo.png" max-width="110" contain style="cursor: pointer" @click="$router.push('/')" /></div>

        <div v-if="customerStore.customer" class="user-card mx-4 mb-4 pa-3">
          <div class="d-flex align-center ga-3">
            <v-avatar size="34" class="user-avatar"><span class="text-caption font-weight-bold" style="color: #0a1628">{{ initials }}</span></v-avatar>
            <div style="min-width: 0; flex: 1">
              <!-- Mitarbeiter (ohne Impersonation): Name + Rolle -->
              <template v-if="customerStore.isEmployee && !customerStore.impersonating">
                <div class="text-white text-body-2 font-weight-medium text-truncate">{{ customerStore.customer.firstname }} {{ customerStore.customer.lastname }}</div>
                <div class="text-caption text-truncate employee-role-label">{{ customerStore.employeeRole === 'admin' ? 'Administrator' : 'Mitarbeiter' }}</div>
              </template>
              <!-- Kunde / Impersonation: Firmenname + ABlyft-ID -->
              <template v-else>
                <div class="text-white text-body-2 font-weight-medium text-truncate">{{ customerStore.customer.company || customerStore.customer.email }}</div>
                <div class="text-caption text-truncate" style="color: rgba(255,255,255,0.4)">{{ customerStore.customer.ablyft_project_id || '—' }}</div>
              </template>
            </div>
          </div>
        </div>

        <!-- Kunden-Menü: für Kunden (nicht-admin) oder während Impersonation -->
        <div v-if="showCustomerMenu" class="nav-section px-3">
          <div class="nav-label">Menü</div>
          <v-list nav density="compact" class="pa-0">
            <v-list-item to="/dashboard" :active="$route.name === 'Dashboard'" class="nav-item" @click="mobile && (drawer = false)">
              <template #prepend><div class="nav-icon-wrap" :class="{ 'nav-icon-active': $route.name === 'Dashboard' }"><v-icon size="18">mdi-view-dashboard-outline</v-icon></div></template>
              <v-list-item-title>Dashboard</v-list-item-title>
            </v-list-item>
            <v-list-item to="/tests" :active="activeNavKey === 'tests'" class="nav-item" @click="mobile && (drawer = false)">
              <template #prepend><div class="nav-icon-wrap" :class="{ 'nav-icon-active': activeNavKey === 'tests' }"><v-icon size="18">mdi-ab-testing</v-icon></div></template>
              <v-list-item-title>A/B Tests</v-list-item-title>
            </v-list-item>
            <v-list-item to="/analytics" :active="activeNavKey === 'analytics'" class="nav-item" @click="mobile && (drawer = false)">
              <template #prepend><div class="nav-icon-wrap" :class="{ 'nav-icon-active': activeNavKey === 'analytics' }"><v-icon size="18">mdi-chart-line</v-icon></div></template>
              <v-list-item-title>Auswertung</v-list-item-title>
            </v-list-item>
          </v-list>
        </div>

        <!-- Verwaltungs-Menü: für Admins und Mitarbeiter (ohne Impersonation) -->
        <div v-if="showAdminMenu" class="nav-section px-3">
          <div class="nav-label">Administration</div>
          <v-list nav density="compact" class="pa-0">
            <v-list-item to="/admin" :active="$route.name === 'Admin'" class="nav-item" @click="mobile && (drawer = false)">
              <template #prepend><div class="nav-icon-wrap nav-icon-admin" :class="{ 'nav-icon-admin-active': $route.name === 'Admin' }"><v-icon size="18">mdi-cog-outline</v-icon></div></template>
              <v-list-item-title>Verwaltung</v-list-item-title>
            </v-list-item>
            <v-list-item to="/ranking" :active="$route.name === 'Ranking'" class="nav-item" @click="mobile && (drawer = false)">
              <template #prepend><div class="nav-icon-wrap nav-icon-admin" :class="{ 'nav-icon-admin-active': $route.name === 'Ranking' }"><v-icon size="18">mdi-podium</v-icon></div></template>
              <v-list-item-title>Ranking</v-list-item-title>
            </v-list-item>
          </v-list>
        </div>
      </div>

      <template v-slot:append>
        <div class="px-3 pb-4">
          <div class="logout-btn" @click="logout"><v-icon size="18" style="color: rgba(255,255,255,0.4)">mdi-logout</v-icon><span>Abmelden</span></div>
        </div>
      </template>
    </v-navigation-drawer>

    <v-main class="bg-main" :style="{ paddingTop: impOffset + 'px' }">
      <div class="content-wrapper pa-4">
        <router-view />
      </div>
    </v-main>
  </v-app>
</template>

<script>
import { useCustomerStore } from "@/stores/customerStore";
export default {
  name: "Navigation",
  setup() { return { customerStore: useCustomerStore() }; },
  data: () => ({ drawer: false, mobile: false }),
  computed: {
    initials() {
      const c = this.customerStore.customer;
      if (!c) return "";
      // Mitarbeiter: Vor-/Nachname; Kunden: Firmenname
      if (this.customerStore.isEmployee && !this.customerStore.impersonating) {
        return ((c.firstname?.charAt(0) || "") + (c.lastname?.charAt(0) || "")).toUpperCase();
      }
      const name = c.company || c.email || "";
      return name.slice(0, 2).toUpperCase();
    },
    activeNavKey() { return this.$route.meta?.navKey || null; },
    impOffset() { return this.customerStore.impersonating ? 44 : 0; },
    // Kunden-Menü anzeigen wenn: kein Admin-Zugang ODER gerade impersoniert
    showCustomerMenu() { return !this.customerStore.canAccessAdmin || this.customerStore.impersonating; },
    // Verwaltungs-Menü anzeigen wenn: Admin-Zugang UND nicht impersoniert
    showAdminMenu() { return this.customerStore.canAccessAdmin && !this.customerStore.impersonating; },
  },
  mounted() { this.checkMobile(); window.addEventListener("resize", this.checkMobile); if (this.customerStore.isAuthenticated) { this.customerStore.fetchMe(); } },
  beforeUnmount() { window.removeEventListener("resize", this.checkMobile); },
  methods: {
    checkMobile() { this.mobile = window.innerWidth < 960; this.drawer = !this.mobile; },
    async logout() { this.drawer = false; await this.customerStore.logout(); this.$router.push("/auth/login"); },
    async exitImpersonate() { this.customerStore.stopImpersonate(); await this.customerStore.fetchMe(); this.$router.push("/admin"); },
  },
};
</script>

<style scoped>
.impersonate-toolbar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 44px;
  z-index: 9999;
  background: #1d3457
}
.imp-inner {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: white;
  font-size: 13px;
  font-weight: 500;
}
.imp-exit {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 5px 14px;
  background: rgba(255,255,255,0.15);
  border: 1px solid rgba(255,255,255,0.25);
  border-radius: 8px;
  color: white;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
  margin-left: 8px;
}
.imp-exit:hover { background: rgba(255,255,255,0.25); }

:deep(.v-application__wrap) { height: 100vh !important; overflow: hidden !important; }
.bg-main { background-color: #f8f9fb !important; height: 100vh !important; overflow-y: auto !important; }
:deep(.v-navigation-drawer) { position: fixed !important; left: 0 !important; overflow-y: auto !important; }
.drawer-custom { border-right: 1px solid rgba(255,255,255,0.06) !important; box-shadow: none !important; }
.drawer-custom :deep(.v-navigation-drawer__border) { display: none !important; }
.drawer-inner { display: flex; flex-direction: column; }
.mobile-app-bar { border-bottom: 1px solid rgba(255,255,255,0.06) !important; }
.user-card { background: rgba(255,255,255,0.05); border-radius: 12px; border: 1px solid rgba(255,255,255,0.06); }
.user-avatar { background: #e8eaed !important; }
.employee-role-label { color: #B2F4AA; font-size: 11px; }
.nav-section { margin-bottom: 8px; }
.nav-label { font-size: 10px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; color: rgba(255,255,255,0.25); padding: 16px 12px 8px; }
.nav-item { min-height: 40px !important; border-radius: 10px !important; margin-bottom: 2px !important; color: rgba(255,255,255,0.55) !important; padding-left: 8px !important; padding-right: 12px !important; transition: all 0.15s ease !important; }
.nav-item :deep(.v-list-item__prepend) { width: auto !important; min-width: auto !important; margin-right: 12px !important; }
.nav-item :deep(.v-list-item-title) { font-size: 13.5px !important; font-weight: 500 !important; color: rgba(255,255,255,0.55) !important; }
.nav-icon-wrap { width: 32px; height: 32px; border-radius: 8px; display: flex; align-items: center; justify-content: center; background: rgba(255,255,255,0.05); transition: all 0.15s; }
.nav-icon-wrap .v-icon { color: rgba(255,255,255,0.45) !important; }
.nav-icon-active { background: rgba(59,130,246,0.2) !important; }
.nav-icon-active .v-icon { color: #60a5fa !important; }
.nav-icon-admin { background: rgba(178,244,170,0.12); }
.nav-icon-admin .v-icon { color: #B2F4AA !important; }
.nav-icon-admin-active { background: rgba(178,244,170,0.2) !important; }
.v-list-item--active { background: rgba(255,255,255,0.08) !important; }
.v-list-item--active :deep(.v-list-item__overlay) { opacity: 0 !important; }
.v-list-item--active :deep(.v-list-item-title) { color: white !important; font-weight: 600 !important; }
.nav-item:hover:not(.v-list-item--active) { background: rgba(255,255,255,0.04) !important; }
.nav-item:hover:not(.v-list-item--active) :deep(.v-list-item__overlay) { opacity: 0 !important; }
.logout-btn { display: flex; align-items: center; gap: 12px; padding: 10px 12px; border-radius: 10px; cursor: pointer; transition: all 0.15s; font-size: 13.5px; font-weight: 500; color: rgba(255,255,255,0.35); border: 1px solid rgba(255,255,255,0.06); }
.logout-btn:hover { background: rgba(239,68,68,0.08); border-color: rgba(239,68,68,0.15); color: #fca5a5; }
.logout-btn:hover .v-icon { color: #fca5a5 !important; }
.content-wrapper { width: 100%; min-height: 100%; }
@media (max-width: 959px) { .content-wrapper { padding: 12px !important; } }
</style>
